import { computed, ref, type ComputedRef } from 'vue'

export type MembershipTier = 'none' | 'monthly' | 'yearly' | 'lifetime'

export interface SubscriptionQueryDto {
  subscription_no: string
  channel: string
  billing_mode: string
  status: string
  amount_minor: number
  currency: string
  billing_period: string
  billing_interval: number
  trial_days: number
  trial_end_at: number | null
  current_period_start: number | null
  current_period_end: number | null
  next_billing_at: number | null
  cancelled_at: number | null
  cancel_at_period_end: number
  channel_subscription_id: string
  billings: Array<Record<string, unknown>>
  /** Animock：`remote=1` 时中台 query 是否已合并进本 DTO */
  remote_sync?: 'skipped' | 'ok' | 'failed'
  /**
   * Animock：本地影子表与中台 webhook 的同步进度。
   *
   * - `1`：影子表 status 已不在 `INCOMPLETE` 系列中（≈ 已稳定）；
   * - `0`：影子表仍处于 `INCOMPLETE` / `INCOMPLETE_EXPIRED`，会员展示宜走「等待」分支。
   *
   * 该字段仅作渲染辅助，不参与权限校验。
   */
  paymid_synced?: 0 | 1
}

export interface OrderListItem {
  biz_order_no: string
  order_type: string
  status: string
  subject: string
  amount_minor: number
  currency: string
  plan_code: string
  created_at: number
  paid_at: number
  pay_order?: {
    pay_order_no?: string
    channel?: string
    status?: string
    amount_minor?: number
    currency?: string
    channel_trade_no?: string
    paid_at?: number
  } | null
}

export interface OrderListPageDto {
  list?: OrderListItem[]
  total?: number
  page?: number
  limit?: number
}

/**
 * 订阅详情弹窗共用的数据加载（订阅查询、订单分页、周期末取消）。
 * 由宿主注入 `requestJson`（编辑器：绝对 URL + `fetch`；营销站：`marketingFetch`）。
 */
export function useSubscriptionDetailApi(options: {
  subscriptionNo: ComputedRef<string>
  /** 无 token 时不发起需登录请求 */
  hasAuth: ComputedRef<boolean>
  requestJson: <T>(args: {
    method: 'GET' | 'POST'
    path: string
    query?: Record<string, string | number | boolean | undefined>
    body?: Record<string, unknown>
  }) => Promise<T>
  onError: (message: string) => void
  /** 取消订阅成功后刷新会员快照（与宿主 `emit('refreshMembership')` 对齐） */
  refreshMembership?: () => Promise<void>
}) {
  const { subscriptionNo, hasAuth, requestJson, onError, refreshMembership } = options

  const loadingSub = ref(false)
  const sub = ref<SubscriptionQueryDto | null>(null)
  /**
   * `subscription/query` 是否仍处于上一次失败状态。
   *
   * 详情弹窗据此切换到「占位渲染 + 重新查询」分支：仍允许用户基于会员快照看到当前套餐与到期日，
   * 并在中台 webhook 落表后重新拉取一次，以便闭环关闭订阅入口。
   */
  const subQueryFailed = ref(false)

  const loadingOrders = ref(false)
  const orders = ref<OrderListItem[]>([])
  const page = ref(1)
  const limit = ref(10)
  const total = ref(0)

  const canceling = ref(false)

  const pageCount = computed(() => {
    const t = total.value
    const l = limit.value
    if (!t || !l) return 1
    return Math.max(1, Math.ceil(t / l))
  })

  type PageItem = number | '...'

  const pageItems = computed<PageItem[]>(() => {
    const current = page.value
    const totalPages = pageCount.value
    const maxButtons = 5
    if (totalPages <= maxButtons) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }
    const items: PageItem[] = []
    const left = Math.max(2, current - 1)
    const right = Math.min(totalPages - 1, current + 1)

    items.push(1)
    if (left > 2) items.push('...')
    for (let p = left; p <= right; p++) items.push(p)
    if (right < totalPages - 1) items.push('...')
    items.push(totalPages)
    return items
  })

  async function loadSubscription(remote: boolean): Promise<void> {
    sub.value = null
    const no = subscriptionNo.value
    if (!no || !hasAuth.value) return
    loadingSub.value = true
    try {
      const dto = await requestJson<SubscriptionQueryDto>({
        method: 'GET',
        path: '/api/subscription/query',
        query: {
          subscription_no: no,
          // 兼容后端历史分支：默认查询不显式传 remote，避免 `remote=0` 触发异常路径。
          remote: remote ? 1 : undefined,
        },
      })
      sub.value = dto
      subQueryFailed.value = false
    } catch (e: unknown) {
      subQueryFailed.value = true
      onError(messageFromUnknown(e))
    } finally {
      loadingSub.value = false
    }
  }

  async function loadOrders(p: number): Promise<void> {
    if (!hasAuth.value) return
    loadingOrders.value = true
    try {
      const dto = await requestJson<OrderListPageDto>({
        method: 'GET',
        path: '/api/payment/order/list',
        query: {
          page: p,
          limit: limit.value,
        },
      })
      orders.value = Array.isArray(dto?.list) ? dto.list : []
      total.value = Number(dto?.total || 0)
      page.value = Number(dto?.page || p)
      limit.value = Number(dto?.limit || limit.value)
    } catch (e: unknown) {
      onError(messageFromUnknown(e))
    } finally {
      loadingOrders.value = false
    }
  }

  function goPage(p: number): void {
    void loadOrders(p)
  }

  async function cancelAtPeriodEnd(afterPost: () => void | Promise<void>): Promise<void> {
    const no = subscriptionNo.value
    if (!no || !hasAuth.value) return
    if (canceling.value) return
    canceling.value = true
    try {
      await requestJson<unknown>({
        method: 'POST',
        path: '/api/subscription/cancel',
        body: {
          subscription_no: no,
          reason: '',
          immediate: 0,
        },
      })
      await afterPost()
      await Promise.all([loadSubscription(true), refreshMembership?.() ?? Promise.resolve()])
    } catch (e: unknown) {
      onError(messageFromUnknown(e))
    } finally {
      canceling.value = false
    }
  }

  return {
    loadingSub,
    sub,
    subQueryFailed,
    loadingOrders,
    orders,
    page,
    limit,
    total,
    canceling,
    pageCount,
    pageItems,
    loadSubscription,
    loadOrders,
    goPage,
    cancelAtPeriodEnd,
  }
}

function messageFromUnknown(e: unknown): string {
  if (e && typeof e === 'object') {
    const rec = e as Record<string, unknown>
    const sm = rec.statusMessage
    if (typeof sm === 'string' && sm.trim()) return sm
    const msg = rec.message
    if (typeof msg === 'string' && msg.trim()) return msg
  }
  if (typeof e === 'string' && e.trim()) return e
  return String(e ?? '')
}
